/* Agentic-hierarchy backbone reads (spec §5.5): signals + sensor availability,
 * wake ledger + DERIVED burn, budget settings. Every absent state has explicit
 * copy in the strip — never a fabricated number, never a fake zero. */
import { request } from './client.ts';

export interface Signal {
  id: number;
  client_slug: string;
  aspect: string;
  kind: string;
  severity: 'info' | 'notable' | 'urgent';
  state: 'new' | 'dispatched' | 'assessed' | 'dismissed' | 'budget-held';
  evidence: Record<string, unknown> | null;
  evidenceCorrupt?: boolean;
  observed_at: string;
  wake_id: number | null;
}

export type SensorAvailability = Record<string, string>;

export interface Wake {
  id: number;
  aspect: string;
  trigger_kind: 'signal' | 'am';
  status: 'running' | 'done' | 'error';
  model: string | null;
  pack_hash: string | null;
  cost_usd: number | null;
  started_at: string;
}

export type DraftLifecycle = 'draft' | 'submitted' | 'responded' | 'published' | 'verified';
export type DraftLifecycleTransition = 'submitted' | 'responded' | 'published';

export interface ExpertCitation {
  url?: string;
  source?: string;
  observedAt?: string;
}

export interface ExpertOutput {
  id: number;
  wake_id: number;
  client_slug: string;
  aspect: string;
  subject_key: string | null;
  kind: 'finding' | 'advisory' | 'draft';
  body: Record<string, unknown>;
  citations: ExpertCitation[];
  lifecycle_state: DraftLifecycle | null;
  lifecycle_changed_at?: string | null;
  status: 'active' | 'superseded' | 'dismissed';
  created_at: string;
}

export interface Burn {
  monthUsd: number;
  monthWakes: number;
  budgetUsd: number;
}

export function getSignals(
  slug: string,
): Promise<{ signals: Signal[]; sensors: SensorAvailability; lastSweptAt: string | null }> {
  return request<{
    signals?: Signal[];
    sensors?: SensorAvailability;
    lastSweptAt?: string | null;
  }>(
    `/clients/${slug}/signals`,
  ).then((r) => ({
    signals: r.signals || [],
    sensors: r.sensors || {},
    lastSweptAt: r.lastSweptAt ?? null,
  }));
}

export function getWakes(slug: string): Promise<{ wakes: Wake[]; burn: Burn | null }> {
  return request<{ wakes?: Wake[]; burn?: Burn | null }>(`/clients/${slug}/experts/wakes`).then(
    (r) => ({ wakes: r.wakes || [], burn: r.burn ?? null }),
  );
}

export function getOutputs(
  slug: string,
  aspect?: string,
): Promise<{ outputs: ExpertOutput[] }> {
  const query = aspect ? `?aspect=${encodeURIComponent(aspect)}` : '';
  return request<{ outputs?: ExpertOutput[] }>(
    `/clients/${encodeURIComponent(slug)}/experts/outputs${query}`,
  ).then((response) => ({ outputs: response.outputs || [] }));
}

export function postWake(
  slug: string,
  aspect: string,
  instruction: string,
  confirmOverBudget = false,
): Promise<{ wake: Wake }> {
  return request<{ wake: Wake }>(
    `/clients/${encodeURIComponent(slug)}/experts/${encodeURIComponent(aspect)}/wake`,
    {
      method: 'POST',
      body: JSON.stringify({ instruction, confirmOverBudget }),
    },
  );
}

export type ExpertOutputPatch =
  | { lifecycleState: DraftLifecycleTransition; status?: never }
  | { status: 'dismissed'; lifecycleState?: never };

export function patchOutput(
  slug: string,
  outputId: number,
  patch: ExpertOutputPatch,
): Promise<{ output: ExpertOutput }> {
  return request<{ output: ExpertOutput }>(
    `/clients/${encodeURIComponent(slug)}/experts/outputs/${outputId}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

/* Probe-evidenced verified (P2 Task 5, spec §5.5): deliberately NOT a value of
 * `ExpertOutputPatch` — "verified" never travels through the AM-driven
 * `onAdvance` transition path, so a caller can't construct a hand-set-verified
 * PATCH by accident. This is the ONLY function that sends it, and it's meant
 * to be called ONLY after `postRecheck` (below) returned an observed verdict;
 * the router's evidence gate (routers/experts.py) is still the real
 * enforcement point — this just keeps the type system honest about where
 * "verified" comes from. */
export function verifyOutput(slug: string, outputId: number): Promise<{ output: ExpertOutput }> {
  return request<{ output: ExpertOutput }>(
    `/clients/${encodeURIComponent(slug)}/experts/outputs/${outputId}`,
    { method: 'PATCH', body: JSON.stringify({ lifecycleState: 'verified' }) },
  );
}

/* Probe-evidenced verified (P2 Task 5, spec §5.5): the "Verify via probe"
 * action runs the SAME deterministic recheck probe the task board uses
 * (POST /clients/{slug}/recheck) — never a click-verify. The router's
 * evidence gate (routers/experts.py) is the source of truth; this is only
 * the transport for the verdict the hub reads to decide whether to attempt
 * the follow-up PATCH lifecycleState="verified" at all. */
export type ProbeVerdict = 'observed' | 'not-observed' | 'unavailable';

export interface RecheckResult {
  verdict: ProbeVerdict;
  evidence: Record<string, unknown> | null;
}

export function postRecheck(slug: string, probe: string, target: string): Promise<RecheckResult> {
  return request<{ verdict?: string; evidence?: Record<string, unknown> | null }>(
    `/clients/${encodeURIComponent(slug)}/recheck`,
    { method: 'POST', body: JSON.stringify({ probe, target }) },
  ).then((response) => ({
    verdict: (response.verdict as ProbeVerdict | undefined) || 'unavailable',
    evidence: response.evidence ?? null,
  }));
}
